import { useEffect, useLayoutEffect, useRef, useState } from "react";
import SvgWrappableText from "../../preview/SvgWrappableText";
import { AlternativesVP } from "../../editor/AlternativesEditor";
import pxFromCm from "../../util/pxFromCm";
import styled from "styled-components";
import { useIsHardNo } from "../../util/useDomain";

interface BrochurePreviewBackProps {
  alternatives: AlternativesVP;
  page4TextTop: string;
  page6TextBottom: string;
  notEuropeanOwned: string;
  europeanOwned: string;
}

export const BrochurePreviewBack: React.FC<BrochurePreviewBackProps> = ({
  alternatives, page4TextTop, page6TextBottom, notEuropeanOwned, europeanOwned
}) => {
  const { isHardNo } = useIsHardNo();
  const [fontSize, setFontSize] = useState(40);
  const [recursion, setRecursion] = useState(0);
  const [page6TextBottomHeight, setPage6TextBottomHeight] = useState(0);
  const alternativesRef = useRef<HTMLDivElement | null>(null);
  const lastCategoryDivRef = useRef<HTMLDivElement | null>(null);

  const width = 702 * 5;
  const hasAlternatives = alternatives && alternatives.categories.length > 0;
  const textColor = "#000000";
  const margin = pxFromCm(0.96);
  const page6TextBottomTop = pxFromCm(17.25) - page6TextBottomHeight;
  const backgroundImage = isHardNo
    ? "url('/hardno_brochure_back_page_no_text.png')"
    : "url('/brochure_back_page_no_text.png')";

  const handlePage6TextBottomLayout = (h, _boxes) => {
    setPage6TextBottomHeight(h);
  }

  useEffect(() => {
    setRecursion(0);
  }, [alternatives]);

  useLayoutEffect(() => {
    if (lastCategoryDivRef && lastCategoryDivRef.current
      && alternativesRef && alternativesRef.current
      && recursion < 20
    ) {
      const alternativesRect = alternativesRef.current.getBoundingClientRect();
      const lastRect = lastCategoryDivRef.current.getBoundingClientRect();
      const lastRight = lastRect.right;
      const lastBottom = lastRect.bottom;
      const footerHeight = 100 + page6TextBottomHeight / 5;
      const remainingHeight = alternativesRect.bottom - lastBottom;
      const usedWidth = lastRight - alternativesRect.left;
      /*console.log("fontSize:", fontSize, "remainingHeight:", remainingHeight, "usedWidth:", usedWidth,
        "page6TextBottomHeight:", page6TextBottomHeight, "recursion:", recursion, "footerHeight:", footerHeight
      );*/
      if (usedWidth > 600 && (usedWidth > 703 || remainingHeight < footerHeight)) {
        if (fontSize > 30) {
          setFontSize(fontSize - (recursion < 10 ? 0.5 : 0.25));
          setRecursion(recursion + 1);
        }
      } else if (usedWidth <= 600 || remainingHeight > (footerHeight + 105)) {
        if (fontSize < 55) {
          setFontSize(fontSize + (recursion < 10 ? 0.5 : 0.25));
          setRecursion(recursion + 1);
        }
      }
    }
  }, [alternatives, fontSize, page6TextBottomHeight])

  return (<>
    <div style={{
      width: "100%",
      height: "100%",
      backgroundImage: backgroundImage,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }} />
    <svg width="100%" height="100%" style={{position: "absolute", top: "0px", left: "0px"}} className="has-custom-font">
    <SvgWrappableText
      text={page6TextBottom}
      color={"#000000"}
      x={width * 2 / 3 + pxFromCm(0.96)}
      width={width / 3 - 2 * pxFromCm(0.96)}
      top={page6TextBottomTop}
      fontSize={43}
      fontWeight="400"
      textAnchor="left"
      onLayout={handlePage6TextBottomLayout}
    />
    {/*
      <BrochurePage6TextBottom
        text={page6TextBottom}
        y={pxFromCm(15.25)}
        height={pxFromCm(2.0)}
      />
      */}
    </svg>
    {hasAlternatives && <div ref={alternativesRef} style={{ 
      color: textColor, padding: `${margin}px`,
      fontSize: `${fontSize}px`,
      position: "absolute", top: "0px", left: "0px", width: "fit-content", height: "100%",
      display: "flex", flexDirection: "column", flexWrap: "wrap",
      alignItems: "start",
      columnGap: `${margin * 2}px`
    }}>
      {true && <PageHeader>{page4TextTop}</PageHeader>}
      {true && <AvoidAndConsiderDiv style={{ borderBottom: `3px solid ${textColor}`}}>
        <AvoidDiv>{notEuropeanOwned}</AvoidDiv>
        <ConsiderDiv>{europeanOwned}</ConsiderDiv>
      </AvoidAndConsiderDiv>}
      {alternatives.categories.map((category, index) => (
        <CategoryDiv className="CategoryDiv" key={index} style={
          true && index < alternatives.categories.length // disabled
            ? { borderBottom: `3px solid ${textColor}`}
            : {}
        }>
          {true && <CategoryHeader style={{
            //borderTop: `3px solid ${textColor}`,
            borderBottom: `3px solid ${textColor}`
          }}>{category.name}</CategoryHeader>}
          <AvoidAndConsiderDiv>
            <AvoidDiv>{category.avoid.map((p) => p.name).join(", ")}</AvoidDiv>
            <ConsiderDiv>{category.consider.map((p) => p.name).join(", ")}</ConsiderDiv>
          </AvoidAndConsiderDiv>
        </CategoryDiv>
      ))}
      <FootnoteContainer
        ref={lastCategoryDivRef}
        style={{
          color: textColor,
          fontSize: fontSize
        }}
      >      
      {alternatives.footnotes.map((f) => (
        <FootnoteDiv key={f}>* {f}</FootnoteDiv>
      ))}
      </FootnoteContainer>
    </div>}
  </>)
}

const PageHeader = styled.div`
  width: calc(7.98 / 21 * 5 * 496px);
  font-family: 'Montserrat';
  text-align: start;
  font-size: 43px;
  line-height: 1.3;
  font-weight: 400;
  padding-bottom: 25px;
`;

const FootnoteContainer = styled.div`
  width: calc(7.98 / 21 * 5 * 496px);
  padding-top: 25px;
  display: flex;
  flex-direction: column;
  gap: 25px;
  align-items: start; 
`;

const FootnoteDiv = styled.div`
  width: calc(7.98 / 21 * 5 * 496px);
  text-align: start;
  line-height: 1.3;
`;

const CategoryDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  width: fit-content;
`;

const CategoryHeader = styled.div`
  width: calc(7.98 / 21 * 5 * 496px);
  padding-top: 5px;
  text-align: start;
  font-weight: 600;
`;

const AvoidAndConsiderDiv = styled.div`
  display: flex;
  align-items: center;
`;

const AvoidDiv = styled.div`
  width: calc(3.7 / 21 * 5 * 496px);
  padding: 10px 20px 10px 0px;
  text-align: start;
`;

const ConsiderDiv = styled.div`
  width: calc(4.28 / 21 * 5 * 496px);
  padding: 10px 0px 10px 0px;
  text-align: start;
`;
